import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SettingsModal } from '@/components/settings';
import { createContext, useCallback, useContext } from 'react';
import { useLocation, useSearch } from 'wouter';
// Map URL paths to settings categories
const pathToCategory = {
    general: 'general',
    display: 'display',
    viewer: 'viewer',
    'hanging-protocols': 'hanging_protocols',
    reporting: 'reporting',
    'teleconsultation-reporting': 'tc_reporting',
    // Alias kept so the id-shaped link in the spec/deep links resolves too.
    tc_reporting: 'tc_reporting',
    schedule: 'schedule',
    users: 'users_permissions',
    'teleconsultation-groups': 'tc_groups',
};
// Map settings categories to URL paths
const categoryToPath = {
    general: 'general',
    display: 'display',
    viewer: 'viewer',
    hanging_protocols: 'hanging-protocols',
    reporting: 'reporting',
    tc_reporting: 'teleconsultation-reporting',
    schedule: 'schedule',
    users_permissions: 'users',
    tc_groups: 'teleconsultation-groups',
};
const SettingsContext = createContext(null);
// Pages that stay mounted under the settings modal (?settings=:category on
// the page's own URL): a route change would tear down the loaded study or
// the report draft, and the dictation help opens settings mid-dictation.
const HOSTED_PATH_RE = /^\/(?:viewer|report|teleconsultation\/report)\/[^/]+$/;
export function SettingsProvider({ children }) {
    const [pathname, setLocation] = useLocation();
    const searchString = useSearch();
    // Settings modal can be driven by /settings/:category (study list underneath) or
    // <host page>?settings=:category so the host stays mounted (see HOSTED_PATH_RE).
    const settingsPathMatch = pathname.match(/^\/settings(?:\/([^/]+))?$/);
    const hostPath = pathname.match(HOSTED_PATH_RE)?.[0];
    const searchParams = new URLSearchParams(searchString);
    const settingsQuery = searchParams.get('settings');
    const settingsOpenFromHost = !!hostPath && settingsQuery !== null && settingsQuery.length > 0;
    const isOpen = !!settingsPathMatch || settingsOpenFromHost;
    const urlCategoryPath = settingsPathMatch
        ? settingsPathMatch[1] || 'general'
        : settingsOpenFromHost && settingsQuery
            ? settingsQuery
            : 'general';
    const category = pathToCategory[urlCategoryPath] || 'general';
    const openSettings = useCallback((initialCategory, options) => {
        const cat = initialCategory || 'general';
        const path = categoryToPath[cat];
        const host = pathname.match(HOSTED_PATH_RE)?.[0];
        if (host) {
            const params = new URLSearchParams(searchString);
            params.set('settings', path);
            if (options?.reportTab)
                params.set('reportTab', options.reportTab);
            else
                params.delete('reportTab');
            if (options?.viewerTab)
                params.set('viewerTab', options.viewerTab);
            else
                params.delete('viewerTab');
            const q = params.toString();
            setLocation(`${host}?${q}`);
            return;
        }
        const tabParams = new URLSearchParams();
        if (options?.reportTab)
            tabParams.set('reportTab', options.reportTab);
        if (options?.viewerTab)
            tabParams.set('viewerTab', options.viewerTab);
        const query = tabParams.size > 0 ? `?${tabParams.toString()}` : '';
        setLocation(`/settings/${path}${query}`);
    }, [pathname, searchString, setLocation]);
    const closeSettings = useCallback(() => {
        const host = pathname.match(HOSTED_PATH_RE)?.[0];
        if (host) {
            const params = new URLSearchParams(searchString);
            if (params.has('settings')) {
                params.delete('settings');
                params.delete('reportTab');
                params.delete('viewerTab');
                const q = params.toString();
                setLocation(q ? `${host}?${q}` : host);
                return;
            }
        }
        setLocation('/');
    }, [pathname, searchString, setLocation]);
    const setCategory = useCallback((newCategory) => {
        const path = categoryToPath[newCategory];
        const host = pathname.match(HOSTED_PATH_RE)?.[0];
        if (host) {
            const params = new URLSearchParams(searchString);
            if (params.has('settings')) {
                params.set('settings', path);
                params.delete('reportTab');
                params.delete('viewerTab');
                const q = params.toString();
                setLocation(`${host}?${q}`);
                return;
            }
        }
        setLocation(`/settings/${path}`);
    }, [pathname, searchString, setLocation]);
    // Handle modal close via the X button or clicking outside
    const handleOpenChange = useCallback((open) => {
        if (!open) {
            closeSettings();
        }
    }, [closeSettings]);
    return (_jsxs(SettingsContext.Provider, { value: { isOpen, category, openSettings, closeSettings, setCategory }, children: [children, _jsx(SettingsModal, { open: isOpen, onOpenChange: handleOpenChange, category: category, onCategoryChange: setCategory })] }));
}
// eslint-disable-next-line react-refresh/only-export-components
export function useSettings() {
    const context = useContext(SettingsContext);
    if (!context) {
        throw new Error('useSettings must be used within a SettingsProvider');
    }
    return context;
}
